import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import type { Dispatch, SetStateAction } from "react";
import ReactTooltip from "react-tooltip";
import {
  CancelButtonSmall,
  GhostButtonMediumV2,
  InvisibleButton,
} from "../Buttons/Buttons";
import { EditIcon, QuoteIcon } from "../Icons/Icons";
import type { IAccordionCardProps } from "./AccordionCard";
import { AccordionCard } from "./AccordionCard";
import { TooltipPortal } from "../TooltipModal/TooltipPortal";
import { spacingTokens } from "../../designTokens";

export const AccordionCardWithEditFeatures = (
  props: Omit<IAccordionCardProps, "actionChildren"> & {
    handleEdit?: () => void;
    handleCancel?: () => void;
    editing?: boolean;
    handleChangeRequest?: () => void;
    editDisabled?: boolean;
    editDisabledReason?: string;
  }
) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const actionChildren = (
    setShowContent: Dispatch<SetStateAction<boolean | undefined>>
  ) => {
    const {
      handleEdit,
      editing,
      handleCancel,
      handleChangeRequest,
      editDisabled,
      editDisabledReason,
    } = props;
    const tooltipId = "accordion-edit-disabled-tooltip";
    return (
      <>
        {handleEdit && !editing && (
          <>
            <span
              data-tip={editDisabled ? editDisabledReason : undefined}
              data-for={
                editDisabled && editDisabledReason ? tooltipId : undefined
              }
            >
              <InvisibleButton
                onClick={() => {
                  if (editDisabled) return;
                  handleEdit();
                  setShowContent(true);
                }}
                disabled={editDisabled}
                style={{
                  display: "flex",
                  gap: "5px",
                  marginRight: "0",
                  paddingRight: "0",
                  opacity: editDisabled ? 0.8 : 1,
                  cursor: editDisabled ? "not-allowed" : undefined,
                  pointerEvents: editDisabled ? "none" : undefined,
                }}
              >
                <EditIcon
                  height={16}
                  width={16}
                  fill={theme.secondaryTextColor}
                />{" "}
                {t("Edit")}
              </InvisibleButton>
            </span>
            {editDisabled && editDisabledReason && (
              <TooltipPortal>
                <ReactTooltip id={tooltipId} place="top" effect="solid" />
              </TooltipPortal>
            )}
          </>
        )}
        {handleChangeRequest && !handleEdit && (
          <GhostButtonMediumV2
            onClick={handleChangeRequest}
            style={{
              marginLeft: spacingTokens.spacing_2xl,
              marginRight: spacingTokens.spacing_4xl,
              display: "flex",
              alignItems: "center",
              padding: 0,
            }}
            aria-label={t("Request Change")}
          >
            <QuoteIcon />
          </GhostButtonMediumV2>
        )}

        {editing && handleCancel && (
          <CancelButtonSmall onClick={handleCancel} />
        )}
      </>
    );
  };
  return (
    <AccordionCard {...props} actionChildren={actionChildren}>
      {props.children}
    </AccordionCard>
  );
};
